/**
 * Invoice totals form: editable totals and accounting conversion, with the paid
 * status and amount shown read-only. State comes from `useInvoiceTotalsFormState`.
 */
import * as React from 'react';
import { Decimal, ONE, parseDecimal, roundToCurrencyPrecision, ZERO } from '@insio/decimal';
import type { CurrencyCode, IDocumentExchangeRate } from '@insio/shared-types';
import { DecimalInput, Input } from '../../../components/inputs';
import { InputWrapper } from '../../../components/InputWrapper';
import { Badge, type BadgeProps } from '../../../components/Badge';
import { CurrencySelector } from '../../../components/selects/CurrencySelector';
import { Divider } from '../../../components/Divider';
import { Button } from '../../../components/Button';
import { Icon } from '../../../components/icons/Icon';
import { AlertBanner } from '../../validation/AlertBanner';
import { FieldErrorList } from '../../validation/FieldErrorList';
import { ExpectedAmountHint } from '../../../components/ExpectedAmountHint';
import { ErrorEntry } from '../../../components/errors';
import { useFormatMoney } from '../../../hooks';
import { useTranslation } from '../../../providers/UIProvider';
import { useInvoiceIsVatDocument } from '../../../providers/InvoiceProvider';
import { IssueCodes } from '@insio/validation';
import { cn, combineIssues, severityStyle } from '../../../utils';
import type { ValidationIssue } from '../../../types';
import type { InvoiceTotalsPaymentStatus, UseInvoiceTotalsFormStateResult } from './hooks';
import { computeInvoiceTotalsExpectation } from './compute-invoice-totals-expectation';

/** Shown for a missing paid value, as the form's empty inputs show it. */
const EMPTY_VALUE = '—';

function formatLabelWithCurrency(label: string, currencyCode?: string | null): string {
  return currencyCode ? `${label} (${currencyCode})` : label;
}

const PAYMENT_STATUS_BADGE_COLOR: Record<InvoiceTotalsPaymentStatus, BadgeProps['color']> = {
  UNPAID: 'neutral',
  PARTIALLY_PAID: 'warning',
  PAID: 'success'
};

/** ISO date (or Temporal.PlainDate) → DD.MM.YY; null when not parseable. */
function formatRateDate(value: unknown): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value ?? ''));
  if (!match) return null;
  return `${match[3]}.${match[2]}.${match[1].slice(2)}`;
}

const SECTION_HEADER_CLASS = 'text-xs font-semibold uppercase tracking-wide text-foreground-muted';

/** Backend warnings comparing the stored totals against the line items. */
const TOTALS_MISMATCH_CODES = new Set<string>([
  IssueCodes.totalAmountNoVatMismatch.code,
  IssueCodes.totalVatAmountMismatch.code,
  IssueCodes.totalAmountMismatch.code
]);

export interface InvoiceTotalsFormData {
  totalAmountNoVat: Decimal;
  totalVatAmount: Decimal;
  totalAmountInclVat: Decimal;
  accountingExchangeRate: IDocumentExchangeRate | null;
  roundingAmount: Decimal | null;
  accountingAmount: Decimal | null;
  accountingAmountNoVat: Decimal | null;
  accountingTotalVatAmount: Decimal | null;
  accountingCurrencyCode: string | null;
}

/** Expected totals from a source (VAT summary or line items), in invoice currency. */
export interface InvoiceTotalsExpected {
  totalAmountNoVat: Decimal;
  totalVatAmount: Decimal;
  totalAmountInclVat: Decimal;
}

export interface InvoiceTotalsFormProps {
  state: UseInvoiceTotalsFormStateResult;
  currencyCode?: string | null;
  /** Accounting currency, used to label the editable accounting amount inputs. */
  accountingCurrencyCode?: string | null;
  /** Totals summed from the VAT summary. When set, a "fill from VAT summary"
   * button is shown. */
  expectedTotalsFromVatSummary?: InvoiceTotalsExpected | null;
  /** Totals summed from the line items. When set, a "fill from line items"
   * button is shown. */
  expectedTotalsFromLines?: InvoiceTotalsExpected | null;
  /** The invoice's paid status, shown read-only. */
  paymentStatus?: InvoiceTotalsPaymentStatus | null;
  /** The invoice's paid amount, shown read-only and subtracted from the total. */
  paidAmount?: Decimal | null;
  /**
   * Show the accounting-exchange-rate input. Hidden by default — the rate is
   * only meaningful when the invoice currency differs from the accounting
   * currency. When hidden, the underlying form state is still preserved and
   * round-trips through `flushAndGet`.
   * @default false
   */
  showAccountingExchangeRate?: boolean;
  /**
   * Suggested CNB/ECB rate; shown as an apply hint under the rate input when
   * the rate is empty (or a placeholder 1).
   */
  suggestedAccountingExchangeRate?: IDocumentExchangeRate | null;
  disabled?: boolean;
  /**
   * Validation issues. Issues with `path === 'totalAmountNoVat'`,
   * `path === 'totalVatAmount'`, `path === 'totalAmountInclVat'`,
   * `path === 'roundingAmount'` or `path === 'accountingExchangeRate'`
   * annotate the corresponding inputs;
   * empty-path issues surface as a top-level banner.
   */
  errors?: ValidationIssue[];
}

export const InvoiceTotalsForm: React.FC<InvoiceTotalsFormProps> = ({
  state,
  currencyCode,
  accountingCurrencyCode,
  expectedTotalsFromVatSummary,
  expectedTotalsFromLines,
  paymentStatus = null,
  paidAmount = null,
  showAccountingExchangeRate = false,
  suggestedAccountingExchangeRate = null,
  disabled = false,
  errors
}) => {
  const t = useTranslation('modules.modals.editInvoiceTotals');
  const tPaymentStatus = useTranslation('components.paymentStatus');
  const isVatDocument = useInvoiceIsVatDocument();
  const currency = currencyCode ?? '';

  // Fill the three totals at once from a source (VAT summary / line items),
  // rounded to currency precision so the saved value matches what's displayed.
  const fillTotals = (expected: InvoiceTotalsExpected) => {
    const cc = (currencyCode ?? undefined) as CurrencyCode | undefined;
    state.setNoVatValue(roundToCurrencyPrecision(expected.totalAmountNoVat, cc));
    state.setVatAmountValue(roundToCurrencyPrecision(expected.totalVatAmount, cc));
    state.setInclVatValue(roundToCurrencyPrecision(expected.totalAmountInclVat, cc));
  };

  // Apply a single validation-issue `meta.expected` value into one total input.
  const applyExpectedTo =
    (setter: (value: Decimal) => void) =>
    (expected: string): void => {
      const parsed = parseDecimal(expected);
      if (parsed == null) return;
      setter(
        roundToCurrencyPrecision(parsed, (currencyCode ?? undefined) as CurrencyCode | undefined)
      );
    };
  // The in-form selection is the source of truth; the prop only seeds the hook.
  const selectedAccountingCurrency = state.accountingCurrencyCode ?? accountingCurrencyCode ?? null;
  const accountingCurrency = selectedAccountingCurrency ?? '';
  const canRecompute = state.effectiveAccountingExchangeRate !== null;
  const formatMoney = useFormatMoney(currencyCode as CurrencyCode | null | undefined);
  const formatAccountingMoney = useFormatMoney(
    selectedAccountingCurrency as CurrencyCode | null | undefined
  );

  // Show the conversion block when the chosen accounting currency differs from
  // the document currency, or when a rate is already present. Driven by the live
  // selection so toggling the currency shows/hides the section immediately.
  // Hidden when both currencies match — the rate is fixed to 1, no conversion.
  const currenciesDiffer = Boolean(
    selectedAccountingCurrency && currencyCode && selectedAccountingCurrency !== currencyCode
  );
  const showConversion =
    !state.currenciesSame &&
    (showAccountingExchangeRate ||
      currenciesDiffer ||
      state.effectiveAccountingExchangeRate !== null);

  // Suggested CNB/ECB rate hint — only when no meaningful rate is entered yet
  // (empty or placeholder 1) and the suggestion would actually change it.
  const suggestedRate = suggestedAccountingExchangeRate;
  const effectiveRate = state.effectiveAccountingExchangeRate;
  const showSuggestedRate = Boolean(
    showConversion &&
    suggestedRate &&
    (effectiveRate === null || effectiveRate.eq(ONE)) &&
    !(effectiveRate !== null && suggestedRate.rate.eq(effectiveRate))
  );
  const suggestedRateDate = formatRateDate(suggestedRate?.rateDate);
  const suggestedRateSource = suggestedRate?.externalSource
    ? t(`rateSource${suggestedRate.externalSource}`)
    : null;

  const remainingToPay = React.useMemo(() => {
    const base = isVatDocument ? state.effectiveInclVat : state.effectiveNoVat;
    return base.minus(paidAmount ?? ZERO);
  }, [isVatDocument, state.effectiveInclVat, state.effectiveNoVat, paidAmount]);

  const grouped = React.useMemo(() => {
    const top: ValidationIssue[] = [];
    const noVat: ValidationIssue[] = [];
    const vat: ValidationIssue[] = [];
    const inclVat: ValidationIssue[] = [];
    const rate: ValidationIssue[] = [];
    const rounding: ValidationIssue[] = [];
    for (const issue of errors ?? []) {
      const path = issue.path ?? '';
      if (path === '') top.push(issue);
      else if (path === 'totalAmountNoVat') noVat.push(issue);
      else if (path === 'totalVatAmount') vat.push(issue);
      else if (path === 'totalAmountInclVat') inclVat.push(issue);
      else if (path === 'accountingExchangeRate') rate.push(issue);
      else if (path === 'roundingAmount') rounding.push(issue);
    }
    return {
      topLevelIssues: top,
      noVatIssues: noVat,
      vatAmountIssues: vat,
      inclVatIssues: inclVat,
      accountingRateIssues: rate,
      roundingAmountIssues: rounding
    };
  }, [errors]);

  const {
    topLevelIssues,
    noVatIssues,
    vatAmountIssues,
    inclVatIssues,
    accountingRateIssues,
    roundingAmountIssues
  } = grouped;

  // The persisted "missing rate" issue is stale the moment the user types a value.
  const visibleAccountingRateIssues =
    state.accountingExchangeRateDisplay.trim() === ''
      ? accountingRateIssues
      : accountingRateIssues.filter(
          issue => issue.code !== IssueCodes.accountingExchangeRateMissing.code
        );

  // Persisted mismatch warnings describe the saved totals, so they are stale as
  // soon as the user edits one — the live expectation below takes over then.
  const dropStaleMismatch = (issues: ValidationIssue[]): ValidationIssue[] =>
    state.totalsTouched ? issues.filter(issue => !TOTALS_MISMATCH_CODES.has(issue.code)) : issues;
  const visibleNoVatIssues = dropStaleMismatch(noVatIssues);
  const visibleVatAmountIssues = dropStaleMismatch(vatAmountIssues);
  const visibleInclVatIssues = dropStaleMismatch(inclVatIssues);

  // Live cross-check: the gross total the other amounts add up to. Derived from
  // the effective values, so the hint tracks typing without a round trip.
  const expectation = React.useMemo(
    () =>
      computeInvoiceTotalsExpectation({
        totalAmountNoVat: state.effectiveNoVat,
        totalVatAmount: state.vatAmount,
        totalAmountInclVat: state.effectiveInclVat,
        roundingAmount: state.effectiveRoundingAmount,
        currencyCode: (currencyCode ?? undefined) as CurrencyCode | undefined
      }),
    [
      state.effectiveNoVat,
      state.vatAmount,
      state.effectiveInclVat,
      state.effectiveRoundingAmount,
      currencyCode
    ]
  );
  // Non-VAT documents render neither the VAT nor the gross input, and their save
  // path mirrors the net total into the gross one — no identity to check there.
  // A field carrying a validation issue keeps its own apply affordance instead.
  const showInclVatHint =
    isVatDocument && expectation.hasMismatch && visibleInclVatIssues.length === 0;

  const noVatSeverity = severityStyle(combineIssues(visibleNoVatIssues)?.severity);
  const vatAmountSeverity = severityStyle(combineIssues(visibleVatAmountIssues)?.severity);
  const inclVatSeverity = severityStyle(combineIssues(visibleInclVatIssues)?.severity);
  const accountingRateSeverity = severityStyle(
    combineIssues(visibleAccountingRateIssues)?.severity
  );
  const roundingAmountSeverity = severityStyle(combineIssues(roundingAmountIssues)?.severity);

  return (
    <div className='bg-background-input rounded-lg border border-border-base overflow-hidden'>
      {(state.error || topLevelIssues.length > 0) && (
        <div className='px-sm py-sm space-y-sm border-b border-border-base'>
          {state.error && <AlertBanner variant='error' title={state.error} />}
          {topLevelIssues.map((issue, i) => (
            <AlertBanner
              key={`top-issue-${i}`}
              variant={issue.severity === 'error' ? 'error' : 'warning'}
              title={<ErrorEntry issue={issue} showActual />}
            />
          ))}
        </div>
      )}

      <div className='p-sm space-y-sm'>
        <section className='space-y-xs'>
          <div className='flex flex-col gap-xs sm:flex-row sm:items-center sm:justify-between sm:gap-sm'>
            <h3 className={SECTION_HEADER_CLASS}>{t('sectionTotals')}</h3>
            <div className='flex flex-wrap items-center gap-xs sm:justify-end'>
              {isVatDocument && expectedTotalsFromVatSummary && (
                <Button
                  variant='ghost'
                  outline='default'
                  size='sm'
                  onClick={() => fillTotals(expectedTotalsFromVatSummary)}
                  disabled={disabled}
                  className='grow sm:grow-0'
                >
                  <Icon name='arrows-rotate' className='w-3.5 h-3.5 mr-xs' />
                  {t('actions.fillFromVatSummary')}
                </Button>
              )}
              {expectedTotalsFromLines && (
                <Button
                  variant='ghost'
                  outline='default'
                  size='sm'
                  onClick={() => fillTotals(expectedTotalsFromLines)}
                  disabled={disabled}
                  className='grow sm:grow-0'
                >
                  <Icon name='arrows-rotate' className='w-3.5 h-3.5 mr-xs' />
                  {t('actions.fillFromLines')}
                </Button>
              )}
            </div>
          </div>
          <div className='grid grid-cols-2 gap-x-sm gap-y-xs'>
            <div data-hover-field='totalAmountNoVat'>
              <InputWrapper
                label={formatLabelWithCurrency(
                  isVatDocument ? t('totalWithoutVat') : t('totalAmount'),
                  currency
                )}
                htmlFor='totals-noVat'
                labelSize='xs'
              >
                <DecimalInput
                  id='totals-noVat'
                  currencyCode={currencyCode as CurrencyCode | null | undefined}
                  value={state.noVatDisplay}
                  onChange={state.setRawNoVat}
                  onBlur={state.commitNoVat}
                  onFocus={e => e.target.select()}
                  disabled={disabled}
                  className={cn(
                    isVatDocument
                      ? 'text-right text-xs font-semibold'
                      : 'text-right text-sm font-bold',
                    noVatSeverity && `border ${noVatSeverity.borderColor}`
                  )}
                />
                <FieldErrorList
                  issues={visibleNoVatIssues}
                  align='right'
                  onApplyExpected={applyExpectedTo(state.setNoVatValue)}
                  applyExpectedLabel={t('actions.applyExpected')}
                />
              </InputWrapper>
            </div>

            {isVatDocument && (
              <div data-hover-field='totalVatAmount'>
                <InputWrapper
                  label={formatLabelWithCurrency(t('vatAmount'), currency)}
                  htmlFor='totals-vatAmount'
                  labelSize='xs'
                >
                  <DecimalInput
                    id='totals-vatAmount'
                    currencyCode={currencyCode as CurrencyCode | null | undefined}
                    value={state.vatAmountDisplay}
                    onChange={state.setRawVatAmount}
                    onBlur={state.commitVatAmount}
                    onFocus={e => e.target.select()}
                    disabled={disabled}
                    className={cn(
                      'text-right text-xs font-semibold',
                      vatAmountSeverity && `border ${vatAmountSeverity.borderColor}`
                    )}
                  />
                  <FieldErrorList
                    issues={visibleVatAmountIssues}
                    align='right'
                    onApplyExpected={applyExpectedTo(state.setVatAmountValue)}
                    applyExpectedLabel={t('actions.applyExpected')}
                  />
                </InputWrapper>
              </div>
            )}

            <div data-hover-field='roundingAmount'>
              <InputWrapper
                label={formatLabelWithCurrency(t('roundingAmount'), currency)}
                htmlFor='totals-roundingAmount'
                labelSize='xs'
              >
                <DecimalInput
                  id='totals-roundingAmount'
                  currencyCode={currencyCode as CurrencyCode | null | undefined}
                  value={state.roundingAmountDisplay}
                  onChange={state.setRawRoundingAmount}
                  onBlur={state.commitRoundingAmount}
                  onFocus={e => e.target.select()}
                  disabled={disabled}
                  placeholder='—'
                  className={cn(
                    'text-right text-xs font-semibold',
                    roundingAmountSeverity && `border ${roundingAmountSeverity.borderColor}`
                  )}
                />
                <FieldErrorList issues={roundingAmountIssues} />
              </InputWrapper>
              {/* Rounding has no expectation of its own; the empty slot keeps this
                  cell's input on the same baseline as its grid partner. Sits outside
                  InputWrapper, which would otherwise clone the input's id onto it. */}
              <div className='mt-xs h-4' aria-hidden />
            </div>

            {isVatDocument && (
              <div data-hover-field='totalAmountInclVat'>
                <InputWrapper
                  label={formatLabelWithCurrency(t('totalAmount'), currency)}
                  htmlFor='totals-inclVat'
                  labelSize='xs'
                >
                  <DecimalInput
                    id='totals-inclVat'
                    currencyCode={currencyCode as CurrencyCode | null | undefined}
                    value={state.inclVatDisplay}
                    onChange={state.setRawInclVat}
                    onBlur={state.commitInclVat}
                    onFocus={e => e.target.select()}
                    disabled={disabled}
                    className={cn(
                      'text-right text-sm font-bold',
                      showInclVatHint && !inclVatSeverity && 'border border-warn-border',
                      inclVatSeverity && `border ${inclVatSeverity.borderColor}`
                    )}
                  />
                  <FieldErrorList
                    issues={visibleInclVatIssues}
                    align='right'
                    onApplyExpected={applyExpectedTo(state.setInclVatValue)}
                    applyExpectedLabel={t('actions.applyExpected')}
                  />
                  <ExpectedAmountHint
                    show={showInclVatHint}
                    label={t('hintExpected')}
                    amount={formatMoney(expectation.expectedInclVat)}
                    onApply={() => state.setInclVatValue(expectation.expectedInclVat)}
                    applyLabel={t('actions.applyExpected')}
                  />
                </InputWrapper>
              </div>
            )}
          </div>
        </section>

        <Divider variant='light' thickness='sm' />

        <section className='space-y-xs'>
          <h3 className={SECTION_HEADER_CLASS}>{t('sectionPayment')}</h3>
          {/* Read-only: the paid state is derived from payments, never typed in. */}
          <dl className='space-y-xs'>
            <div className='flex items-center justify-between gap-sm'>
              <dt className='text-xs font-medium text-foreground-muted'>{t('paymentStatus')}</dt>
              <dd className='text-sm text-right' data-testid='invoice-totals-payment-status'>
                {paymentStatus ? (
                  <Badge size='sm' color={PAYMENT_STATUS_BADGE_COLOR[paymentStatus]}>
                    {tPaymentStatus(paymentStatus)}
                  </Badge>
                ) : (
                  <span className='text-foreground-muted'>{EMPTY_VALUE}</span>
                )}
              </dd>
            </div>
            <div className='flex items-center justify-between gap-sm'>
              <dt className='text-xs font-medium text-foreground-muted'>
                {formatLabelWithCurrency(t('paidAmount'), currency)}
              </dt>
              <dd
                className='text-sm font-semibold text-right'
                data-testid='invoice-totals-paid-amount'
              >
                {paidAmount != null ? (
                  formatMoney(paidAmount)
                ) : (
                  <span className='font-normal text-foreground-muted'>{EMPTY_VALUE}</span>
                )}
              </dd>
            </div>
          </dl>

          <div className='flex items-center justify-between'>
            <span className='text-xs font-medium text-foreground-muted'>
              {formatLabelWithCurrency(t('remainingToPay'), currency)}
            </span>
            <span className='text-sm font-bold text-right'>{formatMoney(remainingToPay)}</span>
          </div>
        </section>

        <Divider variant='light' thickness='sm' />

        <section className='space-y-xs'>
          <div className='flex items-center justify-between'>
            <h3 className={SECTION_HEADER_CLASS}>{t('accountingConversion')}</h3>
            {showConversion && (
              <Button
                variant='ghost'
                size='sm'
                onClick={state.recomputeAccounting}
                disabled={disabled || !canRecompute}
              >
                <Icon name='arrows-rotate' className='w-3.5 h-3.5 mr-xs' />
                {t('recompute')}
              </Button>
            )}
          </div>

          <div className='grid grid-cols-2 gap-x-sm gap-y-xs'>
            <div data-hover-field='accountingCurrencyCode'>
              <InputWrapper
                label={t('accountingCurrency')}
                htmlFor='totals-accountingCurrencyCode'
                labelSize='xs'
              >
                <CurrencySelector
                  id='totals-accountingCurrencyCode'
                  value={state.accountingCurrencyCode}
                  onChange={code => state.setAccountingCurrencyCode(code === '' ? null : code)}
                  disabled={disabled}
                  isSearchable
                  height='md'
                />
              </InputWrapper>
            </div>

            {showConversion && (
              <div data-hover-field='accountingExchangeRate'>
                <InputWrapper
                  label={t('accountingExchangeRate')}
                  htmlFor='totals-accountingExchangeRate'
                  labelSize='xs'
                >
                  <Input
                    id='totals-accountingExchangeRate'
                    type='text'
                    value={state.accountingExchangeRateDisplay}
                    onChange={e => state.setRawAccountingExchangeRate(e.target.value)}
                    onBlur={state.commitAccountingExchangeRate}
                    onFocus={e => e.target.select()}
                    disabled={disabled}
                    placeholder='—'
                    className={cn(
                      'text-right text-xs',
                      accountingRateSeverity && `border ${accountingRateSeverity.borderColor}`
                    )}
                  />
                  <FieldErrorList issues={visibleAccountingRateIssues} />
                  {showSuggestedRate && suggestedRate && (
                    <ExpectedAmountHint
                      show
                      label={t('suggestedRate')}
                      amount={suggestedRate.rate.toString()}
                      formula={
                        suggestedRateSource
                          ? suggestedRateDate
                            ? t('suggestedRatePer', {
                                source: suggestedRateSource,
                                date: suggestedRateDate
                              })
                            : t('suggestedRatePerNoDate', { source: suggestedRateSource })
                          : undefined
                      }
                      onApply={() => state.applyAccountingExchangeRate(suggestedRate.rate)}
                      applyLabel={t('actions.applySuggestedRate')}
                    />
                  )}
                </InputWrapper>
              </div>
            )}
          </div>

          {showConversion && (
            <div className='grid grid-cols-3 gap-x-sm gap-y-xs'>
              <InputWrapper
                label={formatLabelWithCurrency(
                  isVatDocument ? t('withoutVat') : t('total'),
                  accountingCurrency
                )}
                htmlFor='totals-accountingAmountNoVat'
                labelSize='xs'
              >
                <DecimalInput
                  id='totals-accountingAmountNoVat'
                  currencyCode={selectedAccountingCurrency as CurrencyCode | null | undefined}
                  value={state.accountingAmountNoVatDisplay}
                  onChange={state.setRawAccountingAmountNoVat}
                  onBlur={state.commitAccountingAmountNoVat}
                  onFocus={e => e.target.select()}
                  disabled={disabled}
                  placeholder='—'
                  className={isVatDocument ? 'text-right text-xs' : 'text-right text-sm font-bold'}
                />
                <ExpectedAmountHint
                  show={state.accountingMismatches.accountingAmountNoVat}
                  label={t('hintExpected')}
                  amount={formatAccountingMoney(
                    state.expectedAccountingAmounts?.accountingAmountNoVat
                  )}
                  onApply={
                    !disabled && state.expectedAccountingAmounts?.accountingAmountNoVat != null
                      ? () =>
                          state.setRawAccountingAmountNoVat(
                            state.expectedAccountingAmounts!.accountingAmountNoVat!.toString()
                          )
                      : undefined
                  }
                  applyLabel={t('actions.applyExpected')}
                />
              </InputWrapper>

              {isVatDocument && (
                <InputWrapper
                  label={formatLabelWithCurrency(t('vatAmount'), accountingCurrency)}
                  htmlFor='totals-accountingTotalVatAmount'
                  labelSize='xs'
                >
                  <DecimalInput
                    id='totals-accountingTotalVatAmount'
                    currencyCode={selectedAccountingCurrency as CurrencyCode | null | undefined}
                    value={state.accountingTotalVatAmountDisplay}
                    onChange={state.setRawAccountingTotalVatAmount}
                    onBlur={state.commitAccountingTotalVatAmount}
                    onFocus={e => e.target.select()}
                    disabled={disabled}
                    placeholder='—'
                    className='text-right text-xs'
                  />
                  <ExpectedAmountHint
                    show={state.accountingMismatches.accountingTotalVatAmount}
                    label={t('hintExpected')}
                    amount={formatAccountingMoney(
                      state.expectedAccountingAmounts?.accountingTotalVatAmount
                    )}
                    onApply={
                      !disabled && state.expectedAccountingAmounts?.accountingTotalVatAmount != null
                        ? () =>
                            state.setRawAccountingTotalVatAmount(
                              state.expectedAccountingAmounts!.accountingTotalVatAmount!.toString()
                            )
                        : undefined
                    }
                    applyLabel={t('actions.applyExpected')}
                  />
                </InputWrapper>
              )}

              {isVatDocument && (
                <InputWrapper
                  label={formatLabelWithCurrency(t('total'), accountingCurrency)}
                  htmlFor='totals-accountingAmount'
                  labelSize='xs'
                >
                  <DecimalInput
                    id='totals-accountingAmount'
                    currencyCode={selectedAccountingCurrency as CurrencyCode | null | undefined}
                    value={state.accountingAmountDisplay}
                    onChange={state.setRawAccountingAmount}
                    onBlur={state.commitAccountingAmount}
                    onFocus={e => e.target.select()}
                    disabled={disabled}
                    placeholder='—'
                    className='text-right text-sm font-bold'
                  />
                  <ExpectedAmountHint
                    show={state.accountingMismatches.accountingAmount}
                    label={t('hintExpected')}
                    amount={formatAccountingMoney(
                      state.expectedAccountingAmounts?.accountingAmount
                    )}
                    onApply={
                      !disabled && state.expectedAccountingAmounts?.accountingAmount != null
                        ? () =>
                            state.setRawAccountingAmount(
                              state.expectedAccountingAmounts!.accountingAmount!.toString()
                            )
                        : undefined
                    }
                    applyLabel={t('actions.applyExpected')}
                  />
                </InputWrapper>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

InvoiceTotalsForm.displayName = 'InvoiceTotalsForm';
